/**
 * "Rent the look" rail for category pages — rentals-eligible categories
 * only. Eye-catching cyan-accented card placed above the Live strip so
 * buyers see rental options on the same page as buy-flow.
 */
import React from "react";
import { Link } from "react-router-dom";
import { fmtMoney } from "../../lib/money";
import { Calendar, ChevronRight, Star } from "lucide-react";


export function RentTheLookRail({ rentals, categoryId, categoryName }) {
  if (!rentals?.length) return null;
  const minPerDay = Math.min(...rentals.map(r => r.price_per_day)).toFixed(0);
  return (
    <section className="mb-10" data-testid="cat-rentals-rail">
      <div className="relative card overflow-hidden border-[#00F0FF]/40">
        {/* Cyan glow accent strip */}
        <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#00F0FF] via-[#00F0FF]/60 to-transparent"/>
        <div className="p-5 sm:p-6">
          <RentTheLookHeader
            rentalCount={rentals.length}
            categoryId={categoryId}
            categoryName={categoryName}
            minPerDay={minPerDay}/>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 snap-x">
            {rentals.map(r => <RentalCard key={r.product_id} rental={r}/>)}
          </div>
          <div className="sm:hidden mt-3">
            <Link to={`/rentals?category_id=${categoryId}`}
              data-testid="cat-rentals-see-all-mobile"
              className="inline-flex items-center gap-1 text-xs font-bold text-[#00F0FF] uppercase tracking-widest">
              See all rentals <ChevronRight size={12}/>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


function RentTheLookHeader({ rentalCount, categoryId, categoryName, minPerDay }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#00F0FF]/15 flex items-center justify-center shrink-0">
          <Calendar size={18} className="text-[#00F0FF]"/>
        </span>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
              Rent the look<span className="text-[#00F0FF]">.</span>
            </h2>
            <span className="hidden sm:inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-[#00F0FF] text-black px-2 py-0.5 rounded-full">
              New
            </span>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-1">
            Why buy when you can rent? {rentalCount} pieces in {categoryName || "this category"} starting at
            <span className="text-[#00F0FF] font-bold"> {fmtMoney(minPerDay, { decimals: 0 })}/day</span>
          </p>
        </div>
      </div>
      <Link to={`/rentals?category_id=${categoryId}`}
        data-testid="cat-rentals-see-all"
        className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-[#00F0FF] hover:text-[#D4FF00] uppercase tracking-widest shrink-0">
        See all <ChevronRight size={12}/>
      </Link>
    </div>
  );
}


function RentalCard({ rental: r }) {
  return (
    <Link to="/rentals"
      data-testid={`cat-rental-card-${r.product_id}`}
      className="shrink-0 w-[160px] sm:w-[180px] snap-start group">
      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#261534] relative">
        {r.image && <img src={r.image} alt={r.name}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent"/>
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#00F0FF] text-black text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest">
          <Calendar size={9}/> Rent
        </div>
        {r.rating ? (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
            <Star size={8} className="fill-[#D4FF00] text-[#D4FF00]"/>{r.rating.toFixed(1)}
          </div>
        ) : null}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="text-white text-xs font-bold line-clamp-1 mb-0.5">{r.name}</div>
          <div className="flex items-baseline gap-1">
            <span className="text-[#D4FF00] font-display font-black text-sm">{fmtMoney(r.price_per_day, { decimals: 0 })}</span>
            <span className="text-[9px] text-[#A1A1AA]">/day</span>
            {r.deposit > 0 && (
              <span className="text-[9px] text-[#A1A1AA] ml-auto">dep {fmtMoney(r.deposit, { decimals: 0 })}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
